import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Button,
  Group,
  NavLink,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowBackUp,
  IconBrandGoogleDrive,
  IconBuildingFactory2,
  IconClipboardList,
  IconDeviceMobile,
  IconFileText,
  IconLayoutDashboard,
  IconLogout,
  IconPrinter,
  IconQrcode,
  IconSettings,
  IconPackages,
  IconShoppingCart,
  IconSticker,
  IconTruck,
  IconTruckDelivery,
  IconUserDollar,
  IconUsers,
  type Icon,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { matchPath, NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { api } from "../api/client";
import type { CurrentUser } from "../api/types";
import { AppLogo } from "../components/AppLogo";
import { ThemeSwitcher } from "../components/ThemeSwitcher";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { NotificationBell } from "../components/NotificationBell";
import { StationSelector } from "../components/StationSelector";
import { LocalAgentProvider } from "../hooks/useLocalAgent";

interface AppLayoutProps {
  user: CurrentUser;
  onLogout: () => void;
}

interface NavItem {
  to: string;
  labelKey: string;
  icon: Icon;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: IconLayoutDashboard },
  { to: "/customers", labelKey: "nav.customers", icon: IconUsers },
  { to: "/batches", labelKey: "nav.batches", icon: IconBuildingFactory2 },
  { to: "/shipments", labelKey: "nav.shipments", icon: IconTruck },
  { to: "/stock", labelKey: "nav.stock", icon: IconPackages },
  { to: "/free-issues", labelKey: "nav.freeIssues", icon: IconTruckDelivery },
  { to: "/scanner", labelKey: "nav.scanner", icon: IconQrcode },
  { to: "/statie", labelKey: "nav.statie", icon: IconDeviceMobile },
  { to: "/rma", labelKey: "nav.rma", icon: IconArrowBackUp },
  { to: "/godex", labelKey: "nav.godex", icon: IconPrinter },
  { to: "/label-editor", labelKey: "nav.labelEditor", icon: IconSticker },
  { to: "/documents", labelKey: "nav.documents", icon: IconFileText },
  { to: "/audit", labelKey: "nav.audit", icon: IconClipboardList, adminOnly: true },
  { to: "/drive", labelKey: "nav.drive", icon: IconBrandGoogleDrive, adminOnly: true },
  { to: "/procurement", labelKey: "nav.procurement", icon: IconShoppingCart },
  { to: "/workers", labelKey: "nav.workers", icon: IconUserDollar },
  { to: "/settings", labelKey: "nav.settings", icon: IconSettings },
];

const NO_ENTER_ROUTES = ["/scanner", "/statie", "/batches/:id/flash"];

export function AppLayout({ user, onLogout }: AppLayoutProps) {
  const [opened, { toggle, close }] = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  async function handleLogout() {
    await api.logout();
    onLogout();
    navigate("/login", { replace: true });
  }

  return (
    <LocalAgentProvider>
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px={{ base: 8, xs: "md" }} justify="space-between" wrap="nowrap" gap="xs">
          {/* Left side yields space (title ellipsis, then clipped) so the actions on the right never overflow. */}
          <Group gap="xs" wrap="nowrap" className="app-header-brand">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" style={{ flexShrink: 0 }} />
            <div style={{ flexShrink: 0, display: "flex" }}>
              <AppLogo size="sm" />
            </div>
            <Title order={4} className="app-header-title" visibleFrom="xs">
              {t("app.title")}
            </Title>
            <Title order={5} className="app-header-title" hiddenFrom="xs">
              {t("app.titleShort")}
            </Title>
          </Group>
          <Group gap="xs" wrap="nowrap" className="app-header-actions">
            <StationSelector />
            <NotificationBell />
            <ThemeSwitcher compactOnPhone />
            <LanguageSwitcher compactOnPhone />
            <Badge variant="light" visibleFrom="sm">
              {user.role}
            </Badge>
            <Text size="sm" visibleFrom="sm">
              {user.username}
            </Text>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconLogout size={14} />}
              onClick={handleLogout}
              visibleFrom="xs"
            >
              {t("common.logout")}
            </Button>
            <ActionIcon
              variant="subtle"
              size="lg"
              aria-label={t("common.logout")}
              title={t("common.logout")}
              onClick={handleLogout}
              hiddenFrom="xs"
            >
              <IconLogout size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <Stack gap={2}>
          {NAV_ITEMS.filter((it) => !it.adminOnly || user.role === "admin").map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                component={RouterNavLink}
                to={it.to}
                label={t(it.labelKey)}
                leftSection={<Icon size={18} />}
                active={
                  location.pathname === it.to ||
                  (it.to !== "/dashboard" && location.pathname.startsWith(it.to))
                }
                onClick={close}
              />
            );
          })}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <div
          key={location.pathname}
          className={NO_ENTER_ROUTES.some((r) => matchPath(r, location.pathname)) ? undefined : "app-page-enter"}
        >
          <Outlet context={{ user }} />
        </div>
      </AppShell.Main>
    </AppShell>
    </LocalAgentProvider>
  );
}
